import { DomainError, OwnerViewService, } from "@econ-v1/domain";
/**
 * Every query in this file is marked `requiresDurableStorage: false`.
 *
 * `OwnerViewService` is a pass-through over `OwnerViewRepository`, whose port contract
 * (see its doc comment in `@econ-v1/domain`) forbids depending on the durable,
 * OPFS-backed local SQLite cache — the production adapter,
 * `HomeNodeOwnerViewRepository`, resolves every method over the home-node HTTP client.
 * So these four views keep serving when that cache is unavailable (#1661): a browser
 * without OPFS loses offline history, not the ability to see what its home node reports.
 */
const VIEW_INVALIDATIONS = ["domain.entity-changed", "sync.delta-applied"];
const OPERATIONS_INVALIDATIONS = [
    "domain.entity-changed",
    "outbox.state-changed",
    "sync.delta-applied",
    "transport.state-changed",
];
const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 100;
export function createHomeSummaryQuery(service) {
    return {
        invalidateOn: VIEW_INVALIDATIONS,
        requiresDurableStorage: false,
        key: (row) => row.homeNodeId,
        name: "home.summary.v1",
        run: async (params) => {
            parseEmptyParams(params, "home");
            return await service.readHome();
        },
    };
}
export function createPeerDirectoryQuery(service) {
    return {
        invalidateOn: VIEW_INVALIDATIONS,
        requiresDurableStorage: false,
        key: (row) => row.id,
        name: "peers.directory.v1",
        run: async (params) => await service.listPeers(parsePeerDirectoryParams(params)),
    };
}
export function createFeedTimelineQuery(service) {
    return {
        invalidateOn: VIEW_INVALIDATIONS,
        requiresDurableStorage: false,
        key: (row) => row.id,
        name: "feeds.timeline.v1",
        run: async (params) => await service.listFeed(parseFeedQueryParams(params)),
    };
}
export function createOperationalSummaryQuery(service) {
    return {
        invalidateOn: OPERATIONS_INVALIDATIONS,
        requiresDurableStorage: false,
        key: () => "operational-summary",
        name: "operations.summary.v1",
        run: async (params) => {
            parseEmptyParams(params, "operations");
            return await service.readOperational();
        },
    };
}
export function parsePeerDirectoryParams(value) {
    const record = exactRecord(value, ["limit", "status"], "peer directory");
    const limit = parseLimit(record.limit, "peer directory");
    const status = record.status;
    if (status !== undefined && status !== "offline" && status !== "online" && status !== "unknown") {
        invalid("peer directory");
    }
    return {
        ...(limit === undefined ? {} : { limit }),
        ...(status === undefined ? {} : { status }),
    };
}
export function parseFeedQueryParams(value) {
    const record = exactRecord(value, ["before", "limit", "unreadOnly"], "feed");
    const before = record.before;
    if (before !== undefined && typeof before !== "string")
        invalid("feed");
    const limit = parseLimit(record.limit, "feed");
    const unreadOnly = record.unreadOnly;
    if (unreadOnly !== undefined && typeof unreadOnly !== "boolean")
        invalid("feed");
    return {
        ...(before === undefined ? {} : { before }),
        ...(limit === undefined ? {} : { limit }),
        ...(unreadOnly === undefined ? {} : { unreadOnly }),
    };
}
function parseEmptyParams(value, label) {
    exactRecord(value, [], label);
}
function parseLimit(value, label) {
    if (value === undefined)
        return DEFAULT_LIMIT;
    if (typeof value !== "number" || !Number.isInteger(value) || value < 1 || value > MAX_LIMIT)
        invalid(label);
    return value;
}
function exactRecord(value, allowedKeys, label) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        invalid(label);
    const record = value;
    if (Object.keys(record).some((key) => !allowedKeys.includes(key)))
        invalid(label);
    return record;
}
function invalid(label) {
    throw new DomainError({ code: "invalid_request", message: `Invalid ${label} query parameters` });
}
//# sourceMappingURL=owner-views.js.map